import { useIntl } from "react-intl";
import { tableMessages } from "components/shared/lang/tableMessages";
import { SortingOrder } from "elza-api";
import {FC, useCallback, useEffect, useRef, useState, MouseEvent, KeyboardEvent, ReactNode} from 'react';
import { useAppSelector } from 'utils/hooks';
import {StoreHorizontalLoader} from 'components/shared';
import storeFromArea from '../../shared/utils/storeFromArea.jsx';
import { formatPackageSize, formatUnitDate } from './format';
import { findColDefByKey } from './columns';
import './AipTable.scss';
import { useHistory} from 'react-router';
import {urlAip, urlEntity, urlFundAb} from '../../constants.tsx';
import { Link } from 'react-router-dom';
import { useThunkDispatch } from 'utils/hooks';
import {aipsFetchIfNeeded, aipsFilter, aipsFocus, AREA_AIP, AREA_AIPS, setSelectedAips, } from "../../actions/aip/aip.ts";
import {
    MenuCheckedValueChangeData,
    MenuCheckedValueChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableColumnDefinition,
    TableColumnId,
    TableColumnSizingOptions,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableSelectionCell,
    useTableColumnSizing_unstable,
    useTableFeatures,
    useTableSelection,
    useTableSort,
    createTableColumn,
} from '@fluentui/react-components';
import { Icon } from 'components/shared';
import { Button } from 'react-bootstrap';
import { QueueStateCell, getBoolIcon } from './AipCells';
import { explorerPageMessages, linkStateMessages, listMessages, problemMessages } from './messages';
import { addToastrWarning } from '../shared/toastr/ToastrActions';
import { colDef } from './columns';
import { Row } from 'react-bootstrap';
import AipFilterSection from './filter/AipFilterSection.tsx';
import Pagination from 'components/shared/pagination/Pagination.tsx';
import { Aip, AipFilterEntry, Aips } from 'typings/store/index.ts';
import AipDetail from './AipDetail.tsx';
import {AipDetailVO} from "elza-api";

type AipTableProps = {
    onAipSelect?: (id: number) => void;
    filterDisabled?: boolean;
    initialFilters?: AipFilterEntry[];
    hiddenValues?: string[];
    /** Otevření průzkumníka pro jeden AIP; bez něj se sloupec s akcí nezobrazuje. */
    onExplore?: (aipId: number) => void;
    detailOpen?: boolean;
    setDetailOpen?: (open: boolean) => void;
    /** Balíček, na kterém má seznam začít - místo první stránky se otevře ta, na které leží. */
    focusAipId?: number;
    /** Akce stránky nad seznamem; zobrazí se v liště s vyhledáváním, vedle výběru sloupců. */
    toolbarActions?: ReactNode;
}

/**
 * Řádky seznamu AIP ze store; dokud není načteno, je seznam prázdný.
 */
const getAipRows = (aips: Aips) => {
    if(aips.fetched && aips.rows){
        if(
            aips.filter?.from &&
            aips.filter?.pageSize &&
            aips.filter.from > aips.filter.pageSize - 1
        ){
            return aips.rows;
        }
        return [
            ...aips.rows,
        ];
    }

    return [];
};

const AipTable: FC<AipTableProps> = ({onAipSelect, onExplore, filterDisabled, initialFilters, hiddenValues, detailOpen, setDetailOpen, focusAipId, toolbarActions}) => {
    const aips = useAppSelector(state => storeFromArea(state, AREA_AIPS) as Aips);
    const aip = useAppSelector(state => storeFromArea(state, AREA_AIP) as Aip);
    const {from, pageSize} = aips.filter;
    const dispatch = useThunkDispatch();
    const items = getAipRows(aips);
    const history = useHistory();
    const {formatMessage} = useIntl();

    const columnsDef: TableColumnDefinition<AipDetailVO>[] = colDef.map((def) =>
        createTableColumn<AipDetailVO>({
            columnId: def.key,
            renderHeaderCell: () => <>{formatMessage(def.message)}</>,
            renderCell: (item: AipDetailVO) => <>{getContent(item, def.key)}</>,
            compare: (a: AipDetailVO, b: AipDetailVO) => {
                const left = (a as Record<string, any>)[def.key];
                const right = (b as Record<string, any>)[def.key];
                switch(def.valueType) {
                    case "number": return left - right;
                    case "bool": return Number(left) - Number(right);
                    default: return left?.localeCompare(right);
                }
            },
        })
    );

    const [columns, setColumns] = useState<TableColumnDefinition<AipDetailVO>[]>(
        () => columnsDef.filter(col => !findColDefByKey(String(col.columnId))?.hiddenByDefault));

    /** Sloupce maji i skladane klice ("fund.name"), ktere na AipDetailVO primo nejsou. */
    const rawValue = (item: AipDetailVO, key: string): any => (item as Record<string, any>)[key];

    const getContent =(item: AipDetailVO, key: string) => {
        switch(key) {
            case "code": return (
                <span
                    className='link-like'
                    role="link"
                    tabIndex={0}
                    onClick={() => openAip(item.aipId)}
                    onKeyDown={e => { if (e.key === 'Enter') { openAip(item.aipId); } }}
                >
                    {item.code}
                </span>
            );
            case "aipSize": return formatPackageSize(item[key]);
            case "unitdateFrom":  return item.unitdateFrom ? formatUnitDate(item.unitdateFrom, item.unitdateTo): "-";
            // Napojený AIP vede na balíčky svého archivního souboru a rovnou se tam vybere;
            // nedohledaný fond zůstane pomlčkou, není kam odkázat.
            case "fund.name": return item.fund
                ? (
                    <Link to={urlFundAb(item.fund.id, undefined, item.aipId)}
                          title={item.fund.name}
                          onClick={e => e.stopPropagation()}>
                        {item.fund.name}
                    </Link>
                )
                : "-";
            case "fundCode": {
                if (!item.fundCode) {
                    return "-";
                }
                // Dohledaný fond se dá otevřít; nedohledaný zůstane jen kódem z balíčku.
                return item.fund
                    ? (
                        <Link to={urlFundAb(item.fund.id, undefined, item.aipId)}
                              title={item.fund.name}
                              onClick={e => e.stopPropagation()}>
                            {item.fundCode}
                        </Link>
                    )
                    : item.fundCode;
            }
            case "institution.name": return item.institution?.name ?? "-";
            case "institutionCode": {
                if (!item.institutionCode) {
                    return "-";
                }
                // Dohledaná instituce má archivní entitu, na kterou se dá odkázat;
                // nedohledaná zůstane jen kódem z balíčku.
                const accessPointId = item.institution?.accessPointId;
                return accessPointId
                    ? (
                        <Link to={urlEntity(accessPointId)}
                              title={item.institution?.name}
                              onClick={e => e.stopPropagation()}>
                            {item.institutionCode}
                        </Link>
                    )
                    : item.institutionCode;
            }
            case "importState":
                return <QueueStateCell state={item.importState} message={item.importStateMessage} date={item.importStateDate} />;
            case "exportState":
                return <QueueStateCell state={item.exportState} message={item.exportStateMessage} date={item.exportStateDate} />;
            case "linkState": return item.linkState
                ? formatMessage(linkStateMessages[item.linkState]) : "-";
            case "problemType": return item.problemType
                ? (
                    <span className="aip-problem" title={item.problemDescription ?? undefined}>
                        <Icon glyph="fa-exclamation-triangle"/>
                        {formatMessage(problemMessages[item.problemType])}
                    </span>
                )
                : "-";
            default:
                return findColDefByKey(key)?.valueType == "bool"
                    ? getBoolIcon(rawValue(item, key))
                    : rawValue(item, key) ? rawValue(item, key) : "-";
        }
    }


    /**
     * Skok na balíček z adresy. Stránku, na které leží, umí najít jen server, proto se místo
     * obyčejného načtení první stránky zeptáme na ni - jednou, při otevření seznamu.
     */
    const focusPending = useRef(focusAipId != null);
    useEffect(() => {
        if (focusAipId == null) {
            return;
        }
        dispatch(aipsFocus(focusAipId, initialFilters ?? [], aips.filter.pageSize, aips.filter.sort))
            .then((found: boolean) => {
                if (!found) {
                    dispatch(addToastrWarning(formatMessage(listMessages.focusNotFound)));
                }
            })
            .finally(() => { focusPending.current = false; });
        // jen při vstupu na seznam; další stránkování už jde obvyklou cestou
    }, [focusAipId]);

    useEffect(() => {
        // dokud se hledá stránka s balíčkem, načetla by se zbytečně první stránka
        if (!focusPending.current) {
            dispatch(aipsFetchIfNeeded());
        }

        if(hiddenValues) {
            const res = columns.filter(col => !hiddenValues.includes(col.columnId.toString()));
            setColumns(res);
        }
    },[
        aips.filter.from,
        aips.filter.pageSize,
        aips.filter.filters,
        aips.filter.sort,
        dispatch,
    ]);

    /** Vybraný balíček nemusí být na první obrazovce stránky, tak se na něj seznam odroluje. */
    const focusedRow = useRef<HTMLTableRowElement | null>(null);
    useEffect(() => {
        focusedRow.current?.scrollIntoView({block: "center"});
    }, [items]);

    const toggleColumns = (e: MenuCheckedValueChangeEvent, data: MenuCheckedValueChangeData) => {
        setColumns(
            columnsDef.filter((col) =>
                data.checkedItems.some((checked) => checked == def[String(col.columnId)]?.label
            ))
        );
    };

    const handleSelect = (id: number) =>  {
        setDetailOpen?.(true);
        history.push(urlAip(id))
    };

    /**
     * Otevření jednoho AIPu. Seznam u fondu detail nemá, tam vede identifikátor rovnou
     * do průzkumníka; samostatný seznam otevře panel detailu.
     */
    const openAip = (id: number) => onExplore ? onExplore(id) : handleSelect(id);

    const handleChangePage = (nextFrom: number) => nextFrom !== from && dispatch(aipsFilter(aips.filter.filters, nextFrom, aips.filter.pageSize))

    const def = colDef.reduce<Record<string, {label: string; minWidth: number; idealWidth: number}>>((acc, item) => {
        const key = item.key;
        acc[key] = {
            label: formatMessage(item.message),
            minWidth: item.minWidth,
            idealWidth: item.idealWidth
        };
        return acc;
    }, {});


    const [columnSizingOptions, setColumnSizingOptions] = useState<TableColumnSizingOptions>(def);
    const {
        getRows,
        columnSizing_unstable,
        tableRef,
        sort: { getSortDirection, toggleColumnSort, sort },
        selection: {
            allRowsSelected,
            someRowsSelected,
            toggleAllRows,
            toggleRow,
            isRowSelected,
          },
    } = useTableFeatures(
        { columns, items },
        [
            useTableColumnSizing_unstable({ columnSizingOptions, autoFitColumns: false }),
            useTableSort({defaultSortState: { sortColumn: "code", sortDirection: "ascending"}}),
            useTableSelection({
                selectionMode: "multiselect",
                onSelectionChange: (e, data) => {
                    const selectedRows = rows
                        .filter(row => data.selectedItems.has(row.rowId))
                        .map(row => row.item);
                    dispatch(setSelectedAips(selectedRows));
                }
            }),
        ]
      );

    const rows = getRows((row) => {
        const selected = isRowSelected(row.rowId);
        return {
            ...row,
            onClick: (e: MouseEvent) => {
                toggleRow(e, row.rowId);
            },
            onKeyDown: (e: KeyboardEvent) => {
                if (e.key === " ") {
                    e.preventDefault();
                    toggleRow(e, row.rowId);
                }
            },
            selected,
            appearance: selected ? "brand": "none",
        };
    });

    const headerSortProps = (columnId: TableColumnId) => ({
        onClick: (e: MouseEvent) => {
            // a click that ends a column resize must not also sort the column
            if ((e.target as HTMLElement).closest(".fui-TableResizeHandle")) {
                return;
            }
            toggleColumnSort(e, columnId);
            const field = colDef.find(def => def.key === columnId)?.field;
            if (field) {
                const descending = getSortDirection(columnId) === "ascending";
                dispatch(aipsFilter(aips.filter.filters, 0, aips.filter.pageSize,
                    [{field, order: descending ? SortingOrder.Desc : SortingOrder.Asc}]));
            }
        },
        sortDirection: getSortDirection(columnId),
    });

    const toggleAllKeydown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === " ") {
                toggleAllRows(e);
                e.preventDefault();
            }
        },
        [toggleAllRows]
    );

    const handlePageSizeChange = (pageSize:number) => {
        dispatch(aipsFilter(aips.filter.filters, 0, pageSize));
    }

    return (
        <Row className='aip-table'>
            <div className="aip-table-main">
            <StoreHorizontalLoader store={aips} />
            {aips.fetched && (
                <>
                    <AipFilterSection
                        columns={columns.map(item => def[String(item.columnId)]?.label)}
                        onColsChange={toggleColumns}
                        filterDisabled={filterDisabled}
                        initialFilters={initialFilters}
                        hiddenValues={hiddenValues}
                        actions={toolbarActions}
                    />
                    <div className="aip-table-scroll">
                    <Table
                        ref={tableRef}
                        as="table"
                        sortable
                        {...columnSizing_unstable.getTableProps()}
                        className="aip-table-body"
                    >
                        <TableHeader>
                            <TableRow>
                                <TableSelectionCell
                                    checked={allRowsSelected ? true : someRowsSelected ? "mixed" : false}
                                    onClick={toggleAllRows}
                                    onKeyDown={toggleAllKeydown}
                                    checkboxIndicator={{"aria-label": formatMessage(tableMessages.selectAll)}}
                                    className="header"

                                />
                                {onExplore && <TableHeaderCell className="header aip-action-header"/>}
                                {columns.map((column) => (
                                    <TableHeaderCell
                                        key={column.columnId}
                                        {...columnSizing_unstable.getTableHeaderCellProps(column.columnId)}
                                        {...headerSortProps(column.columnId)}
                                        className="header"
                                    >
                                        {column.renderHeaderCell()}
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map(({ item, selected, onClick }) =>  {
                                const isDetailShown = aip?.id == item.aipId;
                                return (
                                <TableRow
                                    key={item.code}
                                    className={isDetailShown ? "table-row detail-shown" : "table-row"}
                                    ref={isDetailShown ? focusedRow : undefined}
                                >
                                    <TableSelectionCell
                                        checked={selected}
                                        checkboxIndicator={{ "aria-label": formatMessage(tableMessages.select) }}
                                        onClick={onClick}
                                    />

                                    {onExplore && (
                                        <TableCell className="aip-action-cell">
                                            <Button variant="action" title={formatMessage(explorerPageMessages.open)}
                                                    onClick={() => onExplore(item.aipId)}>
                                                <Icon glyph="fa-folder-open"/>
                                            </Button>
                                        </TableCell>
                                    )}
                                    {columns.map(col => (
                                        <TableCell
                                            key={`item[${item.code}].${col.columnId}`}
                                            {...columnSizing_unstable.getTableCellProps(col.columnId)}
                                            onClick={() => onAipSelect ? onAipSelect(item.aipId) : handleSelect(item.aipId)}
                                        >
                                            <TableCellLayout truncate>
                                                {col.renderCell(item)}
                                            </TableCellLayout>
                                    </TableCell>
                                    ))}
                                </TableRow>
                            )}
                            )}
                        </TableBody>
                    </Table>
                    </div>
                    <Pagination
                        onPageChange={handleChangePage}
                        from={from}
                        pageSize={pageSize}
                        totalCount={aips.count}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </>
            )
        }
            </div>
            <AipDetail
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
            />
    </Row>
    );
}

export default AipTable;
